import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowUturnLeftIcon, ForwardIcon, BugAntIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/solid';
import Tippy from '@tippyjs/react';

import SentenceDisplay from '../utils/SentenceDisplay';
import PlayButton from '../utils/PlayButton';
import ExamplePlayButton from './ExamplePlayButton';
import Kbd from '../utils/Kbd';
import WordDisplay from '../utils/WordDisplay';
import InteractiveCardHeader from './InteractiveCardHeader';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import GlassyBadge from '../common/GlassyBadge';

const CardActionButton = ({ tooltip, onClick, children, disabled = false, variant, isActive = false, className = '' }) => {
  const getVariantClasses = () => {
    if (variant === 'warning') {
      return isActive ? 'bg-yellow-500/10 text-yellow-500' : 'text-content-secondary hover:bg-yellow-500/10 hover:text-yellow-500';
    }
    return 'text-content-secondary hover:bg-primary/10 hover:text-primary';
  };

  const handleClick = (e) => {
    e.currentTarget.blur();
    onClick?.(e);
  };

  return (
    <Tippy content={tooltip} placement="top" arrow={false} delay={[500, 0]}>
      <button
        onClick={handleClick}
        disabled={disabled}
        className={`
          p-3 rounded-full transition-all duration-200 ease-in-out
          hover:-translate-y-0.5 active:scale-95
          disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-transparent disabled:transform-none
          ${getVariantClasses()}
          ${className}
        `}
      >
        {children}
      </button>
    </Tippy>
  );
};

const SensesList = ({ senses }) => {
  if (!senses || senses.length === 0) {
    return <p className="text-content-secondary text-center p-4">No detailed senses available.</p>;
  }

  const isSingle = senses.length === 1;
  return (
    <div className={`w-full ${isSingle ? 'text-center' : 'text-left'} animate-fade-in p-1`}>
      <div className="space-y-4 divide-y divide-border">
        {senses.map((sense, i) => (
          <div key={i} className={i > 0 ? 'pt-3' : ''}>
            <div className={isSingle ? 'inline-flex text-left' : 'flex'}>
              <div className="mr-3 pt-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-secondary"></div>
              </div>
              <div className="flex-grow">
                <p className="font-semibold text-base text-content-primary">{sense.translation.join('; ')}</p>
                {(sense.parts_of_speech?.length || sense.tags_jisho?.length) > 0 && (
                  <div className="mt-2 space-y-2">
                    {sense.parts_of_speech?.length > 0 && <p className="text-sm text-content-secondary italic">{sense.parts_of_speech.join(', ')}</p>}
                    {sense.tags_jisho?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {sense.tags_jisho.map((tag) => (
                          <GlassyBadge
                            key={tag}
                            color="purple"
                            size="sm"
                          >
                            {tag}
                          </GlassyBadge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const InteractiveCard = ({
  currentItem,
  isSubmitted,
  showFlippingAnimation,
  isIgnored,
  isGraded,
  answerResult,
  showTranslation,
  onItemClick,
  isHotkeysEnabled,
  onIgnore,
  onSkip,
  onReport,
  onSeeAlso,
  onExamples,
  onCulturalContext,
  onPoliteness,
  levelUpAlertState,
  learnedWordIds,
  userInput,
  onVoiceChange,
  currentVoice,
  sessionSettings,
  isTourRunning = false,
  shrinkToContent = false,
}) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const handleShowClick = useCallback(() => setIsRevealed(true), []);
  const [isFlipping, setIsFlipping] = useState(false);
  useEffect(() => {
    if (!isSubmitted) return;
    setIsFlipping(true);
    const t = setTimeout(() => setIsFlipping(false), 700);
    return () => clearTimeout(t);
  }, [isSubmitted]);
  const { isPlaying, currentTime, playSound } = useAudioPlayer();

  // Detect very tight viewports (e.g. landscape phones) to compact the front face.
  // matchMedia uses the CSS layout viewport height, which iOS does NOT change when
  // the keyboard opens — so compact mode only triggers on real orientation changes.
  const [isCompactViewport, setIsCompactViewport] = useState(
    () => window.matchMedia('(max-height: 550px)').matches
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-height: 550px)');
    const update = (e) => setIsCompactViewport(e.matches);
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (isSubmitted && sessionSettings?.autoPlayAfterAnswer) {
      playSound();
    }
  }, [isSubmitted, sessionSettings?.autoPlayAfterAnswer, playSound]);

  // Reset isRevealed when currentItem changes to a different item
  useEffect(() => {
    setIsRevealed(false);
  }, [currentItem?.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key.toLowerCase() === 't' && isHotkeysEnabled && isSubmitted && !showTranslation && !isRevealed) {
        handleShowClick();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isHotkeysEnabled, isSubmitted, showTranslation, isRevealed, handleShowClick]);

  const shouldDisable3D = isTourRunning && isSubmitted;

  const flipperClasses = [
    'w-full',
    !shouldDisable3D && '[transform-style:preserve-3d]',
    'grid',
    isSubmitted && !shouldDisable3D && '[transform:rotateY(180deg)]',
    showFlippingAnimation && !isTourRunning && 'transition-transform duration-700 ease-in-out',
    // During tour, ensure the card itself is clearly positioned without any 3D perspective distortion
    shouldDisable3D && 'relative z-10',
  ]
    .filter(Boolean)
    .join(' ');

  const getCardStateClasses = () => {
    if (isIgnored) return 'border-yellow-500/80 ring-4 ring-yellow-500/20 shadow-2xl shadow-yellow-500/20';
    if (answerResult === 'correct') return 'border-green-500/80 ring-4 ring-green-500/20 shadow-2xl shadow-green-500/20';
    if (answerResult === 'wrong') return 'border-red-500/80 ring-4 ring-red-500/20 shadow-2xl shadow-red-500/20';
    return 'border-border';
  };

  const sectionBorder = 'border-t-2 border-slate-200 dark:border-zinc-700';
  const showTrans = showTranslation || isRevealed;
  const itemType = currentItem?.word ? 'word' : 'sentence';

  // Determine the audio hotkey to display
  const audioHotkey = sessionSettings?.audioHotkeyUseBacktick ? '`' : 'Space';

  return (
    <div className={`${shrinkToContent ? 'w-full' : 'flex-1 min-h-0'} overflow-y-auto overflow-x-hidden${isFlipping ? ' scrollbar-hide' : ''}`}>
      <div className="min-h-full flex flex-col justify-center items-center py-2 px-4">
        <motion.div className={`w-full max-w-4xl flex flex-col ${!shrinkToContent && !isSubmitted && !shouldDisable3D ? 'flex-1 min-h-0 sm:max-h-[30rem]' : ''}`}>
      {/* 2D wrapper explicitly for React-Joyride measurement, immune to 3D bounding box Webkit bugs */}
      <div className={`session-tour-target-wrapper w-full flex flex-col ${!shrinkToContent && !isSubmitted && !shouldDisable3D ? 'flex-1 min-h-0' : ''}`}>
        <div className={`w-full flex-col flex ${!shouldDisable3D ? '[perspective:1000px]' : ''} ${!shrinkToContent && !isSubmitted && !shouldDisable3D ? 'flex-1 min-h-0' : ''}`}>
          <div className={`session-interactive-card ${!shrinkToContent && !isSubmitted && !shouldDisable3D ? 'flex-1 min-h-0' : ''} ${flipperClasses}`}>
            {/* --- FRONT FACE --- */}
            <div
              style={{ transform: shouldDisable3D ? 'none' : 'translateZ(1px)' }}
              className={`
            session-front-face
            ${shouldDisable3D ? 'hidden' : 'flex'}
            [grid-area:1/1] ${!shouldDisable3D ? '[backface-visibility:hidden]' : ''}
            bg-gradient-to-br from-background-secondary to-background-primary
            dark:from-background-primary dark:to-background-secondary
            rounded-2xl shadow-xl flex flex-col items-center justify-center ${isCompactViewport ? 'p-2' : 'p-4'}
            border ${getCardStateClasses()}
            h-full ${isCompactViewport ? 'min-h-0' : 'min-h-[16rem]'}
          `}
            >
              <PlayButton isHotkeysEnabled={isHotkeysEnabled} size={isCompactViewport ? 70 : 120} hideSlider={isCompactViewport} itemData={currentItem} onVoiceChange={onVoiceChange} currentVoice={currentVoice} sessionSettings={sessionSettings} />
              <p className={`${isCompactViewport ? 'mt-1 text-sm' : 'mt-6'} font-semibold text-content-primary`}>
                Press <Kbd textSize={isCompactViewport ? 'text-xs' : 'text-md'}>{audioHotkey}</Kbd> to begin
              </p>
              <p className={`text-content-secondary ${isCompactViewport ? 'text-xs' : ''}`}>or click the button above</p>
              {!isSubmitted && itemType === 'word' && (
                <ExamplePlayButton sentenceId={currentItem.exampleSentenceId} compact={isCompactViewport} />
              )}
            </div>

            {/* --- BACK FACE --- */}
            <div
              className={`
            session-back-face
            relative [grid-area:1/1] ${!shouldDisable3D ? '[backface-visibility:hidden] [transform:rotateY(180deg)]' : ''}
            bg-background-secondary rounded-2xl shadow-xl flex flex-col
            border ${getCardStateClasses()}
            ${isCompactViewport ? 'min-h-0' : 'min-h-[16rem]'}
            ${isSubmitted ? 'sm:h-full sm:overflow-hidden' : 'h-full overflow-hidden'}
          `}
            >
              {isSubmitted && (
                <>
                  <InteractiveCardHeader
                    level={currentItem?.level}
                    levelUpAlertState={levelUpAlertState}
                    isSubmitted={isSubmitted}
                    politeness={currentItem?.politeness}
                    onPolitenessClick={onPoliteness}
                    currentItem={currentItem}
                    onSeeAlso={onSeeAlso}
                    onExamples={onExamples}
                    onCulturalContext={onCulturalContext}
                    reducedAnimations={sessionSettings?.reducedAnimations}
                  />

                  <div className="flex flex-col flex-grow sm:min-h-0">
                    <div className="px-6 pt-4 pb-4 flex-shrink-0 flex flex-col items-center justify-center space-y-4 relative">

                      <div className="pb-3">
                        <PlayButton isHotkeysEnabled={isHotkeysEnabled} size={90} itemData={currentItem} onVoiceChange={onVoiceChange} currentVoice={currentVoice} sessionSettings={sessionSettings} />
                      </div>
                      {itemType === 'sentence' ? (
                        <SentenceDisplay
                          item={currentItem}
                          onItemClick={onItemClick}
                          learnedWordIds={learnedWordIds}
                          currentTime={currentTime}
                          isKaraokeMode={isPlaying && sessionSettings?.showKaraokeMode !== false}
                        />
                      ) : (
                        <WordDisplay item={currentItem} learnedWordIds={learnedWordIds} />
                      )}
                    </div>

                    {/* Translation / Senses Section */}
                    <div className={`flex-grow sm:min-h-0 ${sectionBorder}`}>
                      <div
                        className={`
                      overflow-y-auto sm:h-full
                      ${itemType === 'word' ? 'max-h-[14rem]' : ''}
                    `}
                      >
                        {showTrans ? (
                          itemType === 'sentence' ? (
                            <div className="flex items-center justify-center sm:h-full">
                              <p className="text-xl text-content-primary text-center animate-fade-in p-4">{currentItem?.translation}</p>
                            </div>
                          ) : (
                            <div className="p-4">
                              <SensesList senses={currentItem?.senses} />
                            </div>
                          )
                        ) : (
                          <div className="flex items-center justify-center sm:h-full">
                            <button
                              onClick={handleShowClick}
                              data-tour="show-translation-button"
                              className="
                          flex items-center justify-center gap-2 rounded-lg px-6 py-2 my-2 text-md
                          font-semibold text-white bg-secondary hover:bg-secondary/90
                          shadow transition-colors focus:outline-none focus:ring-2
                          focus:ring-offset-2 focus:ring-secondary focus:ring-offset-background-secondary
                        "
                            >
                              <ChatBubbleBottomCenterTextIcon className="h-5 w-5" />
                              <span className="flex items-center gap-1.5">
                                Show Translation{' '}
                                <Kbd textColor="text-white" borderColor="border-white">
                                  t
                                </Kbd>
                              </span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className={`flex items-center justify-center p-3 ${sectionBorder} rounded-b-2xl`} data-tour="card-actions">
                    <div className="flex items-center gap-2">
                      {onIgnore && (
                        <CardActionButton
                          tooltip={
                            <span className="flex items-center gap-1.5">
                              Ignore Answer <Kbd textColor="text-white">i</Kbd>
                            </span>
                          }
                          onClick={onIgnore}
                          disabled={isIgnored || !isGraded}
                          variant="warning"
                        >
                          <ArrowUturnLeftIcon className={`h-6 w-6 ${isIgnored ? 'text-yellow-500' : ''}`} />
                        </CardActionButton>
                      )}
                      <CardActionButton tooltip="Report Issue" onClick={() => onReport(userInput)}>
                        <BugAntIcon className="h-6 w-6" />
                      </CardActionButton>
                      <CardActionButton tooltip="Skip Item" onClick={onSkip} disabled={isIgnored} className="session-skip-button">
                        <ForwardIcon className="h-6 w-6" />
                      </CardActionButton>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

        </motion.div>
      </div>
    </div>
  );
};

export default InteractiveCard;
